import { useEffect, useMemo, useState, type JSX } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, type TemplateSummary } from '../api/client.js';
import { describeError } from '../lib/describe-error.js';
import { matchesTitleSearch } from '../lib/search.js';

export function Library(): JSX.Element {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [search, setSearch] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState<TemplateSummary | undefined>();
  const [busy, setBusy] = useState(false);

  async function reload(): Promise<void> {
    setLoading(true);
    try {
      setTemplates(await api.listTemplates());
      setError(undefined);
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  const visible = useMemo(
    () => templates.filter((template) => matchesTitleSearch(template.title, search)),
    [templates, search],
  );

  async function duplicate(template: TemplateSummary): Promise<void> {
    setBusy(true);
    try {
      // Fetch then create: the list view carries no body, and the original must
      // be left untouched (FR-019).
      const full = await api.getTemplate(template.templateId);
      await api.createTemplate({ title: `${full.title} (copy)`, body: full.body });
      await reload();
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setBusy(false);
    }
  }

  async function remove(template: TemplateSummary): Promise<void> {
    setBusy(true);
    try {
      await api.deleteTemplate(template.templateId);
      setConfirmingDelete(undefined);
      await reload();
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <p>Loading your templates…</p>;

  return (
    <main className="page">
      <header className="page-header">
        <h1>Your templates</h1>
        <button type="button" className="btn-primary" onClick={() => navigate('/templates/new')}>
          + New template
        </button>
      </header>

      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : null}

      {templates.length === 0 ? (
        // The empty state must explain how to start rather than looking broken
        // (spec Edge Cases).
        <section className="empty-state">
          <h2>No templates yet</h2>
          <p>
            A template is a document you send often — an offer letter, a report, a client update —
            with the parts that change each time marked as fields.
          </p>
          <p>
            Create one, write it the way you want it to read, then use <strong>Insert field</strong>{' '}
            wherever the wording changes.
          </p>
          <button type="button" className="btn-primary" onClick={() => navigate('/templates/new')}>
            Create your first template
          </button>
        </section>
      ) : (
        <>
          <label htmlFor="template-search">Search by title</label>
          <input
            id="template-search"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Filter templates"
          />

          {visible.length === 0 ? (
            <p>No templates match “{search}”.</p>
          ) : (
            <ul className="template-list">
              {visible.map((template) => (
                <li key={template.templateId}>
                  <div>
                    <Link to={`/templates/${template.templateId}`}>{template.title}</Link>
                    <p className="hint">
                      {template.placeholderNames.length === 0
                        ? 'No fields'
                        : `${template.placeholderNames.length} field${template.placeholderNames.length === 1 ? '' : 's'}`}{' '}
                      · changed {new Date(template.updatedAt).toLocaleString()}
                    </p>
                  </div>
                  <div className="row-actions">
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() => navigate(`/templates/${template.templateId}/fill`)}
                    >
                      Fill
                    </button>
                    <button type="button" disabled={busy} onClick={() => void duplicate(template)}>
                      Duplicate
                    </button>
                    <button
                      type="button"
                      className="btn-danger"
                      onClick={() => setConfirmingDelete(template)}
                    >
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {confirmingDelete ? (
        <div className="dialog-backdrop" role="presentation">
          <div
            className="dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-title"
          >
            <h2 id="confirm-title">Delete “{confirmingDelete.title}”?</h2>
            <p>This cannot be undone.</p>
            <div className="dialog-actions">
              <button type="button" onClick={() => setConfirmingDelete(undefined)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger-solid"
                disabled={busy}
                onClick={() => void remove(confirmingDelete)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
}
